import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { io } from 'socket.io-client';
import { tournamentApi, assetUrl } from '../utils/api';
import { trackImpression, trackClick, sponsorRedirectUrl } from '../utils/analytics';

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || '';

// ── Helpers ───────────────────────────────────────────────────
const fmtPts  = v => v != null ? v.toFixed(2) : '—';
const fmtLbs  = v => v != null && v > 0 ? v.toFixed(3) : '—';
const fmtWt   = v => v != null && v > 0 ? v.toFixed(2) : '—';
const rankMedal = r => r === 1 ? '1st' : r === 2 ? '2nd' : r === 3 ? '3rd' : r;

// ── CSS keyframes (injected once) ────────────────────────────
const styleId = 'sb-redesign-styles';
if (typeof document !== 'undefined' && !document.getElementById(styleId)) {
  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
    @keyframes sb-pulse { 0%,100%{opacity:1} 50%{opacity:0.3} }
    @keyframes sb-sponsor-scroll { 0%{transform:translateX(0)} 100%{transform:translateX(-50%)} }
    @keyframes sb-sponsor-delayed { 0%,5%{transform:translateX(0)} 100%{transform:translateX(-50%)} }
    @media (max-width: 640px) {
      .sb-header { padding: 8px 10px 8px !important; }
      .sb-header .sb-topbar { flex-direction: row !important; align-items: center !important; gap: 6px !important; margin-bottom: 8px !important; }
      .sb-header .sb-logo img { height: 24px !important; }
      .sb-header .sb-status-wrap { gap: 4px !important; }
      .sb-header .sb-status-wrap span:first-child { font-size: 7px !important; letter-spacing: 1px !important; }
      .sb-header .sb-status-wrap > div { font-size: 9px !important; padding: 2px 8px !important; }
      .sb-paging-nav button[title] { width: 36px !important; height: 36px !important; }
      .sb-header .sb-event-name { font-size: 22px !important; }
      .sb-sponsor-bar { padding: 0 !important; height: 56px !important; }
      .sb-sponsor-bar .sb-presented { font-size: 8px !important; padding: 0 8px !important; }
      .sb-sponsor-bar .sb-sponsor-logos img { height: 40px !important; }
    }
  `;
  document.head.appendChild(style);
}

// ── Auto-scroll hook ─────────────────────────────────────────
function useAutoScroll(isVisible, durationSec, deps = []) {
  const containerRef = useRef(null);
  const contentRef   = useRef(null);
  const timerRef     = useRef(null);
  const rafRef       = useRef(null);
  const manualRef    = useRef(false);

  useEffect(() => {
    clearTimeout(timerRef.current);
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    manualRef.current = false;

    const cEl = containerRef.current;
    if (cEl) cEl.scrollTop = 0;

    if (!isVisible) return;

    const totalMs  = (durationSec || 10) * 1000;
    const holdMs   = totalMs * 0.35;
    const scrollMs = totalMs * 1.20; // 50% slower than original

    // Detect manual user scroll — disable auto-scroll
    function onUserScroll() {
      manualRef.current = true;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    }
    if (cEl) {
      cEl.addEventListener('wheel', onUserScroll, { passive: true });
      cEl.addEventListener('touchstart', onUserScroll, { passive: true });
    }

    timerRef.current = setTimeout(() => {
      if (!cEl || manualRef.current) return;
      const overflow = cEl.scrollHeight - cEl.clientHeight;
      if (overflow <= 0) return;

      const startTime = performance.now();
      function step(now) {
        if (manualRef.current) return;
        const elapsed = now - startTime;
        const progress = Math.min(elapsed / scrollMs, 1);
        // ease-out cubic
        const eased = 1 - Math.pow(1 - progress, 3);
        cEl.scrollTop = eased * overflow;
        if (progress < 1) {
          rafRef.current = requestAnimationFrame(step);
        }
      }
      rafRef.current = requestAnimationFrame(step);
    }, holdMs);

    return () => {
      clearTimeout(timerRef.current);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (cEl) {
        cEl.removeEventListener('wheel', onUserScroll);
        cEl.removeEventListener('touchstart', onUserScroll);
      }
    };
    // eslint-disable-next-line
  }, [isVisible, durationSec, ...deps]);

  const wrapperStyle = {
    overflowY: 'auto',
    overflowX: 'auto',
    maxHeight: 'calc(100vh - 260px)',
    position: 'relative',
    scrollBehavior: 'auto',
  };

  const innerStyle = {};

  return { containerRef, contentRef, wrapperStyle, innerStyle };
}

// ── DerbyScores Logo ─────────────────────────────────────────
function Logo() {
  return (
    <a href="https://derbyscores.com" style={{ display: 'inline-block' }}>
      <img
        src="/derby-scores-logo.png"
        alt="DerbyScores"
        style={{ height: 42, width: 'auto', objectFit: 'contain' }}
      />
    </a>
  );
}

// ── Scoreboard Status Badge ──────────────────────────────────
function StatusBadge({ event }) {
  const isLive = event.status === 'live';
  const isOfficial = event.status === 'completed';
  const state = isLive ? 'live' : isOfficial ? 'official' : 'not_started';

  const configs = {
    not_started: { bg: '#f0f0f0', color: '#555', border: '1.5px solid #ccc', label: 'NOT STARTED' },
    live: { bg: '#e8f5e9', color: '#1b5e20', border: '1.5px solid #43a047', label: '● LIVE' },
    official: { bg: '#e3f2fd', color: '#0d47a1', border: '1.5px solid #1e88e5', label: '✓ OFFICIAL RESULT' },
  };
  const c = configs[state];

  return (
    <div className="sb-status-wrap" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{ color: '#888', fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', fontFamily: "'Oswald', sans-serif", whiteSpace: 'nowrap' }}>SCOREBOARD STATUS</span>
      <div style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        background: c.bg, color: c.color, border: c.border,
        borderRadius: 20, padding: '4px 14px',
        fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 12, letterSpacing: 1.5, textTransform: 'uppercase',
        whiteSpace: 'nowrap'
      }}>
        {state === 'live' && (
          <span style={{
            display: 'inline-block', width: 7, height: 7, borderRadius: '50%', background: '#43a047',
            animation: 'sb-pulse 1.4s ease-in-out infinite'
          }} />
        )}
        {state === 'live' ? 'LIVE' : c.label}
      </div>
    </div>
  );
}

// ── Single tournament scoreboard ─────────────────────────────
function TournamentBoard({ board, event, isVisible, duration }) {
  const { tournament, day_tournaments, teams = [], type, big_fish_leader, big_fish_max, big_fish_species } = board;
  const isMultiDay = day_tournaments && day_tournaments.length > 1;
  const isWeightOnly = type === 'weight_only';
  const isManual = tournament.scoring_mode === 'manual';
  // Per-tournament scoreboard_columns (fall back to event-level flags for backwards compat)
  // Per-tournament scoreboard_columns override everything when explicitly set
  const cols_cfg = board.scoreboard_columns || tournament.scoreboard_columns || {};
  const hasCfg = Object.keys(cols_cfg).length > 0;
  const showTeamName  = cols_cfg.team_name !== false;
  const showTeamNum   = cols_cfg.team_number != null ? cols_cfg.team_number : (event.scoreboard_show_team_number !== false);
  const showFishCount = hasCfg ? (cols_cfg.fish_count !== false) : !isWeightOnly;
  const showSpecies   = cols_cfg.species != null ? cols_cfg.species : !!tournament.track_species;
  const showWeight    = cols_cfg.weight !== false;
  const showPoints    = hasCfg ? (cols_cfg.points !== false && !isWeightOnly) : !isWeightOnly;
  const showSpread    = cols_cfg.spread != null ? cols_cfg.spread : (event.scoreboard_show_spread !== false);
  const showBigFish   = (cols_cfg.big_fish != null ? cols_cfg.big_fish : ((event.scoreboard_show_big_fish !== false) && !isWeightOnly)) && tournament.has_big_fish_contest;

  // Weight formatting with configurable decimal places
  const wd = tournament.weight_decimals ?? 2;
  const fmtW  = v => v != null && v > 0 ? v.toFixed(wd) : '—';
  const fmtBF = v => v != null && v > 0 ? v.toFixed(wd) : '—';

  const { containerRef, contentRef, wrapperStyle, innerStyle } =
    useAutoScroll(isVisible, duration, [teams.length]);

  const scoreLabel = isWeightOnly ? 'WEIGHT' : 'SCORE';
  const scoreUnit  = isWeightOnly ? 'lbs' : 'pts';
  const entityLabel = event.event_type === 'individual' ? 'PARTICIPANT' : 'TEAM NAME';

  // Compute per-day big fish leaders for multi-day tournaments
  const dayBFLeaders = {};
  if (isMultiDay && showBigFish && day_tournaments) {
    day_tournaments.forEach((dt, di) => {
      let maxBF = 0, leaderId = null;
      teams.forEach(t => {
        const bf = t.days[di]?.heaviest_fish || 0;
        if (bf > maxBF) { maxBF = bf; leaderId = t.team_id; }
      });
      if (maxBF > 0) dayBFLeaders[di] = leaderId;
    });
  }

  const podiumBg = (pos) => {
    if (pos === 1) return 'rgba(232, 168, 56, 0.09)';
    if (pos === 2) return 'rgba(180, 180, 190, 0.06)';
    if (pos === 3) return 'rgba(185, 130, 80, 0.06)';
    return 'transparent';
  };

  // Determine visible columns for first/last th border-radius
  const cols = [];
  cols.push('pos');
  if (showTeamNum) cols.push('team_number');
  if (showTeamName) cols.push('team_name');
  if (isMultiDay) {
    (day_tournaments || []).forEach(d => {
      if (showFishCount) cols.push(`fish_d${d.day_number}`);
      if (showWeight) cols.push(`wt_d${d.day_number}`);
      if (showBigFish) cols.push(`bf_d${d.day_number}`);
      if (showPoints) cols.push(`score_d${d.day_number}`);
    });
    cols.push('overall');
    if (showSpread) cols.push('spread');
  } else {
    if (showFishCount) cols.push('fish_count');
    if (showWeight) cols.push('total_weight');
    if (showBigFish) cols.push('big_fish');
    if (showPoints) cols.push('score');
    if (showSpread) cols.push('spread');
  }

  const thBase = (colKey) => {
    const isFirst = cols[0] === colKey;
    const isLast = cols[cols.length - 1] === colKey;
    return {
      padding: '12px 10px',
      textAlign: 'left',
      fontFamily: "'Oswald', sans-serif",
      fontWeight: 700,
      fontSize: 11,
      letterSpacing: 1.5,
      textTransform: 'uppercase',
      color: 'var(--th-color)',
      background: 'var(--th-bg)',
      ...(isFirst ? { borderRadius: '8px 0 0 8px', paddingLeft: 16 } : {}),
      ...(isLast ? { borderRadius: '0 8px 8px 0', paddingRight: 16 } : {}),
    };
  };

  const tdBase = (extra = {}) => ({
    padding: '14px 10px',
    textAlign: 'left',
    fontFamily: "'Oswald', sans-serif",
    fontWeight: 500,
    fontSize: 15,
    color: 'var(--row-color)',
    ...extra,
  });

  const unitLabel = (text) => (
    <span style={{ display: 'block', fontFamily: 'Inter, sans-serif', fontSize: 10, color: '#bbb' }}>{text}</span>
  );

  const dash = <span style={{ color: '#ccc' }}>—</span>;

  return (
    <div style={{ opacity: isVisible ? 1 : 0, transition: 'opacity 0.5s ease' }}>
      {teams.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: '#5c6d83', fontSize: '1rem', fontFamily: "'Oswald', sans-serif" }}>
          No catches logged yet — leaderboard updates live
        </div>
      ) : (
        <div ref={containerRef} style={{ ...wrapperStyle, overflowX: 'auto' }}>
          <div ref={contentRef} style={innerStyle}>
          <table className="scoreboard-table" style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
            <thead>
              <tr>
                <th style={thBase('pos')}>POS</th>
                {showTeamNum && <th style={thBase('team_number')}>{event.event_type === 'individual' ? '#' : 'TEAM #'}</th>}
                {showTeamName && <th style={{ ...thBase('team_name'), textAlign: 'left' }}>{entityLabel}</th>}

                {isMultiDay ? (
                  (day_tournaments || []).map(day => (
                    <React.Fragment key={day.id}>
                      {showFishCount && <th style={thBase(`fish_d${day.day_number}`)}>FISH D{day.day_number}</th>}
                      {showWeight && <th style={thBase(`wt_d${day.day_number}`)}>WT D{day.day_number}</th>}
                      {showBigFish && <th style={thBase(`bf_d${day.day_number}`)}>BF D{day.day_number}</th>}
                      {showPoints && <th style={thBase(`score_d${day.day_number}`)}>{scoreLabel} D{day.day_number}</th>}
                    </React.Fragment>
                  ))
                ) : (
                  <>
                    {showFishCount && <th style={thBase('fish_count')}>FISH</th>}
                    {showWeight && <th style={thBase('total_weight')}>WEIGHT</th>}
                    {showBigFish && <th style={thBase('big_fish')}>BIG FISH</th>}
                    {showPoints && <th style={thBase('score')}>{scoreLabel}</th>}
                  </>
                )}

                {isMultiDay && <th style={thBase('overall')}>OVERALL</th>}
                {showSpread && <th style={thBase('spread')}>SPREAD</th>}
              </tr>
            </thead>
            <tbody>
              {teams.map((team, idx) => {
                const isBFLeader = team.team_id === big_fish_leader && big_fish_max > 0;
                const pos = team.rank;
                const isLast = idx === teams.length - 1;

                return (
                  <tr key={team.team_id} style={{
                    background: podiumBg(pos),
                    borderBottom: isLast ? 'none' : '1px solid var(--row-border)',
                    transition: 'background 0.3s'
                  }}>
                    {/* POS */}
                    <td style={tdBase({ textAlign: 'left', paddingLeft: 16 })}>
                      {pos != null ? (
                        pos <= 3
                          ? <span style={{ fontSize: 22 }}>{rankMedal(pos)}</span>
                          : <span style={{ fontWeight: 600, fontSize: 15, color: 'var(--row-color)' }}>{pos}</span>
                      ) : dash}
                    </td>

                    {/* TEAM # */}
                    {showTeamNum && (
                      <td style={tdBase({ fontWeight: 600, fontSize: 13, color: '#e8a838' })}>
                        {team.team_number ? `#${team.team_number}` : dash}
                      </td>
                    )}

                    {/* TEAM NAME */}
                    {showTeamName && (
                    <td style={tdBase({ textAlign: 'left', fontWeight: 700, fontSize: 15, color: 'var(--row-color)' })}>
                      {team.team_name}
                    </td>
                    )}

                    {/* Day data */}
                    {isMultiDay ? (
                      team.days.map((day, dayIdx) => (
                        <React.Fragment key={day.tournament_id}>
                          {showFishCount && (
                            <td style={tdBase()}>
                              {day.total_fish ?? dash}
                              {dayIdx === 0 && showSpecies && team.species_catches && team.species_catches.length > 0 && (
                                <div style={{ fontFamily: 'Inter, sans-serif', fontSize: 9, color: '#8a9bb5', marginTop: 2, lineHeight: 1.3 }}>
                                  {team.species_catches.map(sc => sc.species).join(', ')}
                                </div>
                              )}
                            </td>
                          )}
                          {showWeight && (
                            <td style={tdBase()}>
                              {day.total_weight > 0 ? <><span style={{ display: 'block' }}>{fmtW(day.total_weight)}</span>{unitLabel('lbs')}</> : dash}
                            </td>
                          )}
                          {showBigFish && (() => {
                            const isDayBFLeader = dayBFLeaders[dayIdx] === team.team_id && day.heaviest_fish > 0;
                            return (
                            <td style={tdBase({ color: isDayBFLeader ? '#ffffff' : 'rgb(0, 89, 160)', fontWeight: isDayBFLeader ? 800 : 600 })}>
                              {day.heaviest_fish > 0
                                ? isDayBFLeader
                                  ? <span style={{ display: 'inline-block', background: '#d32f2f', color: '#ffffff', borderRadius: 8, padding: '4px 10px' }}>
                                      <span style={{ display: 'block', fontWeight: 800 }}>{fmtBF(day.heaviest_fish)}</span>
                                      <span style={{ display: 'block', fontFamily: 'Inter, sans-serif', fontSize: 9, letterSpacing: 0.5 }}>&#x25C6; BIG FISH</span>
                                    </span>
                                  : <><span style={{ display: 'block' }}>{fmtBF(day.heaviest_fish)}</span>{unitLabel('lbs')}</>
                                : dash}
                            </td>
                            );
                          })()}
                          {showPoints && (
                            <td style={tdBase({ color: '#e8a838', fontWeight: 800, fontSize: 16 })}>
                              {day.total_points != null
                                ? <><span style={{ display: 'block' }}>{fmtPts(day.total_points)}</span>{unitLabel(scoreUnit)}</>
                                : dash}
                            </td>
                          )}
                        </React.Fragment>
                      ))
                    ) : (
                      <>
                        {/* FISH */}
                        {showFishCount && (
                          <td style={tdBase()}>
                            {(team.days[0]?.total_fish || 0) > 0
                              ? <>
                                  <span style={{ fontWeight: 500, fontSize: 15 }}>{team.days[0].total_fish}</span>
                                  {showSpecies && team.species_catches && team.species_catches.length > 0 && (
                                    <div style={{ fontFamily: 'Inter, sans-serif', fontSize: 9, color: '#8a9bb5', marginTop: 2, lineHeight: 1.3 }}>
                                      {team.species_catches.map(sc => sc.species).join(', ')}
                                    </div>
                                  )}
                                </>
                              : dash}
                          </td>
                        )}
                        {/* WEIGHT */}
                        {showWeight && (
                          <td style={tdBase()}>
                            {team.days[0]?.total_weight > 0
                              ? <><span style={{ display: 'block', fontWeight: 500, fontSize: 15 }}>{fmtW(team.days[0].total_weight)}</span>{unitLabel('lbs')}</>
                              : dash}
                          </td>
                        )}
                        {/* BIG FISH */}
                        {showBigFish && (
                          <td style={tdBase({
                            color: isBFLeader ? '#ffffff' : 'rgb(0, 89, 160)',
                            fontWeight: isBFLeader ? 800 : 600,
                            fontSize: 15,
                          })}>
                            {team.days[0]?.heaviest_fish > 0
                              ? isBFLeader
                                ? <span style={{ display: 'inline-block', background: '#d32f2f', color: '#ffffff', borderRadius: 8, padding: '4px 10px' }}>
                                    <span style={{ display: 'block', fontWeight: 800 }}>{fmtBF(team.days[0].heaviest_fish)}</span>
                                    <span style={{ display: 'block', fontFamily: 'Inter, sans-serif', fontSize: 9, letterSpacing: 0.5 }}>&#x25C6; BIG FISH</span>
                                  </span>
                                : <><span style={{ display: 'block' }}>{fmtBF(team.days[0].heaviest_fish)}</span>{unitLabel('lbs')}</>
                              : dash}
                          </td>
                        )}
                        {/* SCORE */}
                        {showPoints && (
                          <td style={tdBase({ color: '#e8a838', fontWeight: 800, fontSize: 16 })}>
                            {team.has_score
                              ? <><span style={{ display: 'block' }}>{fmtPts(team.overall)}</span>{unitLabel(scoreUnit)}</>
                              : dash}
                          </td>
                        )}
                      </>
                    )}

                    {/* Overall (multi-day) */}
                    {isMultiDay && (
                      <td style={tdBase({ color: '#e8a838', fontWeight: 800, fontSize: 16 })}>
                        {team.has_score
                          ? isWeightOnly
                            ? <><span style={{ display: 'block' }}>{fmtW(team.overall_weight)}</span>{unitLabel('lbs')}</>
                            : <><span style={{ display: 'block' }}>{fmtPts(team.overall)}</span>{unitLabel(scoreUnit)}</>
                          : dash}
                      </td>
                    )}

                    {/* Spread */}
                    {showSpread && (
                      <td style={tdBase({ fontSize: 13 })}>
                        {team.spread != null && team.spread !== 0
                          ? `(${Math.abs(team.spread).toFixed(isWeightOnly ? wd : 2)})`
                          : '—'}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Custom Daily Winners board ──────────────────────────────
function DailyWinnersBoard({ board, event, isVisible, duration }) {
  const { tournament, daily_results = [] } = board;
  const wd = tournament.weight_decimals ?? 2;
  const cols_cfg = board.scoreboard_columns || tournament.scoreboard_columns || {};
  const showTeamNum = cols_cfg.team_number != null ? cols_cfg.team_number : true;

  const { containerRef, contentRef, wrapperStyle, innerStyle } =
    useAutoScroll(isVisible, duration, [daily_results.length]);

  return (
    <div style={{ opacity: isVisible ? 1 : 0, transition: 'opacity 0.5s ease' }}>
      {tournament.min_weight_lbs && (
        <div style={{ textAlign: 'center', marginBottom: 12 }}>
          <div style={{ fontFamily: "'Oswald', sans-serif", color: '#8a9bb5', fontSize: '0.72rem' }}>
            Min Weight: {tournament.min_weight_lbs} lbs
          </div>
        </div>
      )}
      {daily_results.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: '#5c6d83' }}>No days configured yet</div>
      ) : (
        <div ref={containerRef} style={{ ...wrapperStyle, maxWidth: 700, margin: '0 auto' }}>
          <div ref={contentRef} style={innerStyle}>
            <table className="scoreboard-table" style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
              <thead>
                <tr style={{ background: 'var(--th-bg)' }}>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontFamily: "'Oswald', sans-serif", fontWeight: 700, letterSpacing: 1.5, fontSize: 11, color: 'var(--th-color)', textTransform: 'uppercase', borderRadius: '8px 0 0 8px' }}>DAY</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontFamily: "'Oswald', sans-serif", fontWeight: 700, letterSpacing: 1.5, fontSize: 11, color: 'var(--th-color)', textTransform: 'uppercase' }}>WINNER</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontFamily: "'Oswald', sans-serif", fontWeight: 700, letterSpacing: 1.5, fontSize: 11, color: 'var(--th-color)', textTransform: 'uppercase', borderRadius: '0 8px 8px 0' }}>WEIGHT</th>
                </tr>
              </thead>
              <tbody>
                {daily_results.map((day, idx) => (
                  <tr key={day.tournament_id} style={{
                    borderBottom: idx === daily_results.length - 1 ? 'none' : '1px solid var(--row-border)',
                    background: day.winner ? 'transparent' : 'rgba(0,0,0,0.02)'
                  }}>
                    <td style={{ padding: '14px 16px', fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 15, color: 'var(--row-color)' }}>
                      Day {day.day_number}
                      {day.is_rolled_over && (
                        <span style={{
                          display: 'inline-block', marginLeft: 8, background: '#ff9f0a', color: '#fff',
                          borderRadius: 10, padding: '2px 8px', fontSize: 9, fontWeight: 700, letterSpacing: 1, verticalAlign: 'middle'
                        }}>ROLLOVER</span>
                      )}
                    </td>
                    <td style={{ padding: '14px 16px', fontFamily: "'Oswald', sans-serif", fontWeight: 600, fontSize: 15, color: 'var(--row-color)' }}>
                      {day.winner ? (
                        <>
                          {showTeamNum && day.winner.team_number && <span style={{ color: '#e8a838', marginRight: 8, fontSize: 13 }}>#{day.winner.team_number}</span>}
                          {day.winner.team_name}
                        </>
                      ) : (
                        <span style={{ color: '#ccc', fontStyle: 'italic' }}>
                          {day.is_rolled_over ? 'No qualifier — rolled over' : 'Pending'}
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '14px 16px', textAlign: 'left', fontFamily: "'Oswald', sans-serif", fontWeight: 800, fontSize: 16, color: '#e8a838' }}>
                      {day.winner ? `${day.winner.weight_lbs.toFixed(wd)} lbs` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Per-catch board (custom + weight-only + day disabled) ────
function PerCatchBoard({ board, event, isVisible, duration }) {
  const { tournament, catch_entries = [] } = board;
  const cols_cfg = board.scoreboard_columns || tournament.scoreboard_columns || {};
  const showTeamNum = cols_cfg.team_number !== false;
  const showSpecies = cols_cfg.species != null ? cols_cfg.species : !!tournament.track_species;
  const showFishCount = cols_cfg.fish_count !== false;

  const wd = tournament.weight_decimals ?? 2;
  const fmtW = v => v != null ? v.toFixed(wd) : '';

  // catch_entries is now [{team_id, team_name, team_number, catches: [{weight_lbs, species, fish_count}]}]
  const maxCatches = catch_entries.reduce((max, t) => Math.max(max, t.catches.length), 0);

  const { containerRef, contentRef, wrapperStyle, innerStyle } =
    useAutoScroll(isVisible, duration, [catch_entries.length]);

  const thStyle = { padding: '12px 8px', fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 10, letterSpacing: 1.2, textTransform: 'uppercase', color: 'var(--th-color)', background: 'var(--th-bg)', textAlign: 'center', whiteSpace: 'nowrap' };
  const tdStyle = { padding: '12px 8px', fontFamily: "'Oswald', sans-serif", fontWeight: 500, fontSize: 13, color: 'var(--row-color)', textAlign: 'center' };

  return (
    <div style={{ opacity: isVisible ? 1 : 0, transition: 'opacity 0.5s ease' }}>
      {catch_entries.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: '#5c6d83', fontSize: '1rem', fontFamily: "'Oswald', sans-serif" }}>
          No catches logged yet — leaderboard updates live
        </div>
      ) : (
        <div ref={containerRef} style={{ ...wrapperStyle, overflowX: 'auto' }}>
          <div ref={contentRef} style={innerStyle}>
          <table className="scoreboard-table" style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
            <thead>
              <tr>
                <th style={{ ...thStyle, borderRadius: '8px 0 0 8px', paddingLeft: 14 }}>POS</th>
                {showTeamNum && <th style={thStyle}>TEAM #</th>}
                <th style={{ ...thStyle, textAlign: 'left' }}>TEAM</th>
                {Array.from({ length: maxCatches }, (_, i) => (
                  <React.Fragment key={i}>
                    {showFishCount && <th style={thStyle}>FISH ({i + 1})</th>}
                    {showSpecies && <th style={thStyle}>SP ({i + 1})</th>}
                    <th style={thStyle}>WT ({i + 1})</th>
                  </React.Fragment>
                ))}
                <th style={{ ...thStyle, borderRadius: '0 8px 8px 0', paddingRight: 14, fontWeight: 800, color: '#e8a838' }}>TOTAL</th>
              </tr>
            </thead>
            <tbody>
              {catch_entries.map((team, idx) => (
                <tr key={team.team_id} style={{ borderBottom: idx === catch_entries.length - 1 ? 'none' : '1px solid var(--row-border)' }}>
                  <td style={{ ...tdStyle, fontWeight: 700, fontSize: 15, paddingLeft: 14 }}>{idx + 1}</td>
                  {showTeamNum && (
                    <td style={{ ...tdStyle, fontWeight: 600, color: '#e8a838' }}>
                      {team.team_number ? `#${team.team_number}` : '—'}
                    </td>
                  )}
                  <td style={{ ...tdStyle, textAlign: 'left', fontWeight: 700, fontSize: 14 }}>
                    {team.team_name}
                  </td>
                  {Array.from({ length: maxCatches }, (_, i) => {
                    const c = team.catches[i];
                    return (
                      <React.Fragment key={i}>
                        {showFishCount && <td style={tdStyle}>{c ? c.fish_count : ''}</td>}
                        {showSpecies && <td style={{ ...tdStyle, fontSize: 12 }}>{c ? (c.species || '—') : ''}</td>}
                        <td style={{ ...tdStyle, fontWeight: 600 }}>{c ? fmtW(c.weight_lbs) : ''}</td>
                      </React.Fragment>
                    );
                  })}
                  <td style={{ ...tdStyle, fontWeight: 800, fontSize: 15, color: '#e8a838', paddingRight: 14 }}>
                    {fmtW(team.catches.reduce((s, c) => s + c.weight_lbs, 0))}
                    <span style={{ fontFamily: 'Inter, sans-serif', fontSize: 10, color: '#bbb', marginLeft: 3 }}>lbs</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Calcutta board ────────────────────────────────────────────
function CalcuttaBoard({ board, event, isVisible, duration }) {
  const { tournament, rankings = [] } = board;
  const tracks = tournament.calcutta_tracks || 'overall';
  const trackLabel = { overall: 'Overall Standing', heaviest_fish: 'Heaviest Single Fish', heaviest_box: 'Heaviest Box', day_winner: `Day ${tournament.calcutta_day} Winner` };

  // Inherit column settings from the source tournament
  const cols_cfg = board.scoreboard_columns || {};
  const showTeamNum = cols_cfg.team_number != null ? cols_cfg.team_number : (event.scoreboard_show_team_number !== false);
  const wd = tournament.weight_decimals ?? 2;
  const entityLabel = event.event_type === 'individual' ? 'PARTICIPANT' : 'TEAM';

  const { containerRef, contentRef, wrapperStyle, innerStyle } =
    useAutoScroll(isVisible, duration, [rankings.length]);

  return (
    <div style={{ opacity: isVisible ? 1 : 0, transition: 'opacity 0.5s ease' }}>
      <div style={{ textAlign: 'center', marginBottom: 16 }}>
        <div style={{ fontFamily: "'Oswald', sans-serif", color: '#e8a838', fontSize: 'clamp(0.7rem, 1.4vw, 0.9rem)', fontWeight: 700, letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: 4 }}>
          CALCUTTA
        </div>
        <div style={{ fontFamily: "'Oswald', sans-serif", color: '#0d1b2a', fontSize: 'clamp(1rem, 2.5vw, 1.6rem)', fontWeight: 700, letterSpacing: '0.02em' }}>
          {tournament.name.replace(/\s*\(Day\s*\d+\)\s*$/, '')}
        </div>
        <div style={{ fontFamily: "'Oswald', sans-serif", color: '#8a9bb5', fontSize: '0.72rem', marginTop: 3 }}>
          Tracking: {trackLabel[tracks] || tracks}
        </div>
      </div>
      {rankings.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: '#5c6d83' }}>Waiting for main event results...</div>
      ) : (
        <div ref={containerRef} style={{ ...wrapperStyle, maxWidth: 600, margin: '0 auto', overflowX: 'auto' }}>
          <div ref={contentRef} style={innerStyle}>
          <table className="scoreboard-table" style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
            <thead>
              <tr style={{ background: 'var(--th-bg)' }}>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontFamily: "'Oswald', sans-serif", fontWeight: 700, letterSpacing: 1.5, fontSize: 11, color: 'var(--th-color)', textTransform: 'uppercase', borderRadius: '8px 0 0 8px' }}>POS</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontFamily: "'Oswald', sans-serif", fontWeight: 700, letterSpacing: 1.5, fontSize: 11, color: 'var(--th-color)', textTransform: 'uppercase' }}>{entityLabel}</th>
                <th style={{ padding: '12px 16px', textAlign: 'center', fontFamily: "'Oswald', sans-serif", fontWeight: 700, letterSpacing: 1.5, fontSize: 11, color: 'var(--th-color)', textTransform: 'uppercase', borderRadius: '0 8px 8px 0' }}>{trackLabel[tracks]?.toUpperCase() || 'VALUE'}</th>
              </tr>
            </thead>
            <tbody>
              {rankings.map((r, idx) => (
                <tr key={r.team_id} style={{ background: idx < 3 ? (idx === 0 ? 'rgba(232,168,56,0.09)' : idx === 1 ? 'rgba(180,180,190,0.06)' : 'rgba(185,130,80,0.06)') : 'transparent', borderBottom: idx === rankings.length - 1 ? 'none' : '1px solid var(--row-border)' }}>
                  <td style={{ padding: '14px 16px', fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: r.rank <= 3 ? 22 : 15, color: 'var(--row-color)' }}>
                    {r.rank <= 3 ? rankMedal(r.rank) : r.rank}
                  </td>
                  <td style={{ padding: '14px 16px', fontFamily: "'Oswald', sans-serif", fontWeight: 600, color: 'var(--row-color)', fontSize: 15 }}>
                    {showTeamNum && r.team_number ? <span style={{ color: '#e8a838', marginRight: 8, fontSize: 13 }}>#{r.team_number}</span> : null}
                    {r.team_name}
                  </td>
                  <td style={{ padding: '14px 16px', textAlign: 'center', fontFamily: "'Oswald', sans-serif", fontWeight: 800, color: '#e8a838', fontSize: 16 }}>
                    {r.value > 0 ? r.value.toFixed(wd) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Calcutta Daily Heaviest Fish board ───────────────────────
function CalcuttaDailyBoard({ board, event, isVisible, duration }) {
  const { tournament, daily_results = [] } = board;
  const cols_cfg = board.scoreboard_columns || {};
  const showTeamNum = cols_cfg.team_number != null ? cols_cfg.team_number : (event.scoreboard_show_team_number !== false);
  const wd = tournament.weight_decimals ?? 2;
  const entityLabel = event.event_type === 'individual' ? 'PARTICIPANT' : 'TEAM';

  const { containerRef, contentRef, wrapperStyle, innerStyle } =
    useAutoScroll(isVisible, duration, [daily_results.length]);

  return (
    <div style={{ opacity: isVisible ? 1 : 0, transition: 'opacity 0.5s ease' }}>
      <div style={{ textAlign: 'center', marginBottom: 16 }}>
        <div style={{ fontFamily: "'Oswald', sans-serif", color: '#e8a838', fontSize: 'clamp(0.7rem, 1.4vw, 0.9rem)', fontWeight: 700, letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: 4 }}>
          CALCUTTA
        </div>
        <div style={{ fontFamily: "'Oswald', sans-serif", color: '#0d1b2a', fontSize: 'clamp(1rem, 2.5vw, 1.6rem)', fontWeight: 700, letterSpacing: '0.02em' }}>
          {tournament.name.replace(/\s*\(Day\s*\d+\)\s*$/, '')}
        </div>
        <div style={{ fontFamily: "'Oswald', sans-serif", color: '#8a9bb5', fontSize: '0.72rem', marginTop: 3 }}>
          Heaviest Single Fish — Per Day
        </div>
      </div>
      {daily_results.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: '#5c6d83' }}>Waiting for results...</div>
      ) : (
        <div ref={containerRef} style={{ ...wrapperStyle, maxWidth: 700, margin: '0 auto' }}>
          <div ref={contentRef} style={innerStyle}>
            <table className="scoreboard-table" style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
              <thead>
                <tr style={{ background: 'var(--th-bg)' }}>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontFamily: "'Oswald', sans-serif", fontWeight: 700, letterSpacing: 1.5, fontSize: 11, color: 'var(--th-color)', textTransform: 'uppercase', borderRadius: '8px 0 0 8px' }}>DAY</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontFamily: "'Oswald', sans-serif", fontWeight: 700, letterSpacing: 1.5, fontSize: 11, color: 'var(--th-color)', textTransform: 'uppercase' }}>{entityLabel}</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontFamily: "'Oswald', sans-serif", fontWeight: 700, letterSpacing: 1.5, fontSize: 11, color: 'var(--th-color)', textTransform: 'uppercase', borderRadius: '0 8px 8px 0' }}>WEIGHT</th>
                </tr>
              </thead>
              <tbody>
                {daily_results.map((day, idx) => (
                  <tr key={day.tournament_id || idx} style={{
                    borderBottom: idx === daily_results.length - 1 ? 'none' : '1px solid var(--row-border)',
                    background: day.winner ? 'transparent' : 'rgba(0,0,0,0.02)'
                  }}>
                    <td style={{ padding: '14px 16px', fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 15, color: 'var(--row-color)' }}>
                      Day {day.day_number}
                    </td>
                    <td style={{ padding: '14px 16px', fontFamily: "'Oswald', sans-serif", fontWeight: 600, fontSize: 15, color: 'var(--row-color)' }}>
                      {day.winner ? (
                        <>
                          {showTeamNum && day.winner.team_number && <span style={{ color: '#e8a838', marginRight: 8, fontSize: 13 }}>#{day.winner.team_number}</span>}
                          {day.winner.team_name}
                          {day.winner.species && <span style={{ color: '#8a9bb5', fontSize: 11, marginLeft: 8 }}>({day.winner.species})</span>}
                        </>
                      ) : (
                        <span style={{ color: '#ccc', fontStyle: 'italic' }}>Pending</span>
                      )}
                    </td>
                    <td style={{ padding: '14px 16px', textAlign: 'left', fontFamily: "'Oswald', sans-serif", fontWeight: 800, fontSize: 16, color: '#e8a838' }}>
                      {day.winner ? `${day.winner.weight_lbs.toFixed(wd)} lbs` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Advert Board (HTML content display) ──────────────────────
function AdvertBoard({ board, isVisible }) {
  const iframeRef = useRef(null);

  useEffect(() => {
    if (!iframeRef.current || !board.content_html) return;
    const doc = iframeRef.current.contentDocument;
    if (!doc) return;
    doc.open();
    doc.write(`<!DOCTYPE html>
<html><head><style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  html, body { width: 100%; height: 100%; overflow: hidden; background: #000; color: #fff; font-family: 'Oswald', 'Segoe UI', sans-serif; }
  img { max-width: 100%; height: auto; }
  video { max-width: 100%; height: auto; }
</style></head><body>${board.content_html}</body></html>`);
    doc.close();
    const videos = doc.querySelectorAll('video');
    videos.forEach(v => { v.muted = true; v.play().catch(() => {}); });
  }, [board.content_html]);

  return (
    <div style={{ opacity: isVisible ? 1 : 0, transition: 'opacity 0.5s ease', width: '100%', height: '100vh' }}>
      <iframe
        ref={iframeRef}
        title={board.title || 'Advertisement'}
        sandbox="allow-same-origin allow-scripts allow-popups allow-popups-to-escape-sandbox"
        style={{
          width: '100%', height: '100%', border: 'none',
          background: '#000'
        }}
      />
    </div>
  );
}

// ── Sponsor Banner ───────────────────────────────────────────
function SponsorBanner({ sponsors, eventId }) {
  if (!sponsors || sponsors.length === 0) return null;
  // Ensure enough items for seamless loop
  let loopItems = [...sponsors];
  while (loopItems.length < 10) loopItems = [...loopItems, ...sponsors];
  const doubled = [...loopItems, ...loopItems];

  return (
    <div className="sb-sponsor-bar" style={{
      display: 'flex', alignItems: 'stretch',
      position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 20,
      height: 80, overflow: 'hidden',
      borderTop: '5px solid #0d1b2a'
    }}>
      <div className="sb-presented" style={{
        background: '#0d1b2a',
        fontFamily: "'Oswald', sans-serif", fontWeight: 600, fontSize: 10, letterSpacing: 2,
        color: '#ffffff', textTransform: 'uppercase', whiteSpace: 'nowrap',
        display: 'flex', alignItems: 'center',
        padding: '0 20px', flexShrink: 0
      }}>
        PRESENTED BY
      </div>
      <div style={{ overflow: 'hidden', flex: 1, position: 'relative', background: '#ffffff', display: 'flex', alignItems: 'center' }}>
        <div className="sb-sponsor-logos" style={{
          display: 'flex', alignItems: 'center', gap: 40,
          animation: 'sb-sponsor-delayed 100s linear infinite',
          width: 'max-content', padding: '0 20px'
        }}>
          {doubled.map((s, i) => {
            const hasLink = s.website_url && s.id && eventId;
            const Wrapper = hasLink ? 'a' : 'span';
            const wrapperProps = hasLink ? {
              href: sponsorRedirectUrl(s.id, eventId),
              target: '_blank',
              rel: 'noopener noreferrer',
              onClick: () => trackClick({ eventId, clickType: 'sponsor', sponsorId: s.id, pageContext: 'scoreboard' })
            } : {};
            return (
            <Wrapper key={`${s.id || s.sponsor_name}-${i}`} {...wrapperProps} style={{ display: 'flex', alignItems: 'center', flexShrink: 0, textDecoration: 'none', cursor: hasLink ? 'pointer' : 'default' }}>
              {s.logo_url ? (
                <img src={assetUrl(s.logo_url)} alt={s.sponsor_name || s.name}
                  style={{ height: 65, width: 'auto', maxWidth: 160, objectFit: 'contain' }}
                  onError={e => { e.target.style.display = 'none'; }}
                />
              ) : (
                <span style={{
                  background: '#f0f0f0', border: '1px solid #ddd',
                  borderRadius: 6, padding: '6px 16px',
                  fontFamily: "'Oswald', sans-serif", fontWeight: 600, fontSize: 12, color: '#888'
                }}>
                  {s.sponsor_name || s.name}
                </span>
              )}
            </Wrapper>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Main Scoreboard page ──────────────────────────────────────
export default function Scoreboard() {
  const { eventId } = useParams();
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [connected, setConnected] = useState(false);
  const [activeBoardIdx, setActiveBoardIdx] = useState(0);
  const [visible, setVisible]   = useState(true);
  const rotationRef    = useRef(null);
  const socketRef      = useRef(null);
  const activeAdBoardRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const { data: res } = await tournamentApi.getScoreboard(eventId);
      setData(res.data);
    } catch (err) {
      console.error('Scoreboard fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Track scoreboard impression on mount (backend dedups per IP per day)
  useEffect(() => {
    if (eventId) trackImpression(eventId);
  }, [eventId]);

  // WebSocket live updates
  useEffect(() => {
    const socket = io(SOCKET_URL, { transports: ['websocket'] });
    socketRef.current = socket;
    socket.on('connect', () => { setConnected(true); socket.emit('join_scoreboard', eventId); });
    socket.on('disconnect', () => setConnected(false));
    socket.on('scoreboard_update', update => { if (update.eventId === eventId) fetchData(); });
    return () => socket.disconnect();
  }, [eventId, fetchData]);

  // Build unified board array: tournament boards + advert boards
  const allBoards = React.useMemo(() => {
    if (!data) return [];
    const tBoards = (data.tournament_boards || []).map(b => ({
      ...b, _kind: 'tournament', _duration: data.event.scoreboard_rotation_seconds || 10
    }));
    const aBoards = (data.advert_boards || []).map(b => ({
      ...b, _kind: 'advert', _duration: b.display_duration_seconds || 30
    }));
    return [...tBoards, ...aBoards];
  }, [data]);

  // Auto-rotation with per-board duration
  useEffect(() => {
    if (allBoards.length <= 1) return;

    function scheduleNext() {
      const currentBoard = allBoards[activeBoardIdx] || allBoards[0];
      const duration = (currentBoard?._duration || 10) * 1000;

      rotationRef.current = setTimeout(() => {
        setVisible(false);
        setTimeout(() => {
          setActiveBoardIdx(prev => (prev + 1) % allBoards.length);
          setVisible(true);
        }, 500);
      }, duration);
    }

    scheduleNext();
    return () => clearTimeout(rotationRef.current);
  }, [allBoards, activeBoardIdx]);

  // Keep ref updated with current active advert board (for iframe click detection)
  useEffect(() => {
    const board = allBoards[activeBoardIdx] || allBoards[0];
    activeAdBoardRef.current = board?._kind === 'advert' ? board : null;
  }, [allBoards, activeBoardIdx]);

  // Detect clicks inside advert board iframes via window blur
  useEffect(() => {
    function handleBlur() {
      const board = activeAdBoardRef.current;
      if (board) {
        trackClick({ eventId, clickType: 'advert', advertBoardId: board.id, pageContext: 'advert_board' });
      }
    }
    window.addEventListener('blur', handleBlur);
    return () => window.removeEventListener('blur', handleBlur);
  }, [eventId]);

  if (loading) return (
    <div style={{ background: '#ffffff', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div className="spinner" />
    </div>
  );
  if (!data) return (
    <div style={{ background: '#ffffff', color: '#8a9bb5', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem', fontFamily: "'Oswald', sans-serif" }}>
      Event not found
    </div>
  );

  const event   = data.event;
  const sponsors = data.sponsors || [];
  const activeBoard = allBoards[activeBoardIdx] || allBoards[0];
  const isAdBoard = activeBoard?._kind === 'advert';
  const hasSponsorBar = sponsors.length > 0;

  return (
    <div style={{
      minHeight: '100vh',
      background: isAdBoard ? '#000' : '#ffffff',
      color: '#0d1b2a',
      fontFamily: "'Oswald', sans-serif",
      overflow: 'hidden',
      position: 'relative',
      paddingBottom: isAdBoard ? 0 : (hasSponsorBar ? 90 : 20),
      transition: 'background 0.5s ease'
    }}>

      {/* Header zone (white, not customizable) */}
      {!isAdBoard && (
        <div className="sb-header" style={{
          background: '#ffffff',
          borderBottom: '3px solid #0d1b2a',
          padding: '20px 24px 16px'
        }}>
          {/* Top bar: logo left, status right */}
          <div className="sb-topbar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
            <span className="sb-logo"><Logo /></span>
            <StatusBadge event={event} />
          </div>

          {/* Centre: event + tournament identity */}
          <div style={{ textAlign: 'center' }}>
            <div className="sb-event-name" style={{ fontSize: 34, fontWeight: 700, color: '#0d1b2a', textTransform: 'uppercase', letterSpacing: 1.5 }}>
              {event.name}
            </div>
            <div style={{
              display: 'inline-block', background: '#0d1b2a', color: '#e8a838',
              fontSize: 11, fontWeight: 600, letterSpacing: 2.5, textTransform: 'uppercase',
              borderRadius: 20, padding: '5px 16px', marginTop: 8
            }}>
              MAIN TOURNAMENT
            </div>
            {allBoards.length > 1 ? (() => {
              const goTo = (idx) => { clearTimeout(rotationRef.current); setVisible(false); setTimeout(() => { setActiveBoardIdx(idx); setVisible(true); }, 300); };
              const goPrev = () => goTo((activeBoardIdx - 1 + allBoards.length) % allBoards.length);
              const goNext = () => goTo((activeBoardIdx + 1) % allBoards.length);
              const arrowBtn = { background: 'rgba(0,0,0,0.06)', border: '1px solid rgba(0,0,0,0.1)', borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, padding: 0, transition: 'background 0.2s', flexShrink: 0 };
              const arrowSvg = (dir) => (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0d1b2a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  {dir === 'left' ? <polyline points="15 18 9 12 15 6" /> : <polyline points="9 6 15 12 9 18" />}
                </svg>
              );
              return (
                <div className="sb-paging-nav" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, margin: '10px auto 6px' }}>
                  <button onClick={goPrev} style={arrowBtn} title="Previous board">{arrowSvg('left')}</button>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    {allBoards.map((b, i) => {
                      const isAd = b._kind === 'advert';
                      const isActive = i === activeBoardIdx;
                      return (
                        <button key={i} onClick={() => goTo(i)}
                          style={{
                            width: isActive ? 24 : 12, height: 12,
                            borderRadius: isAd ? '50%' : 6,
                            background: isActive ? (isAd ? '#ff9f0a' : '#e8a838') : (isAd ? 'rgba(255,159,10,0.3)' : 'rgba(0,0,0,0.12)'),
                            border: isActive ? '2px solid #0d1b2a' : '1px solid rgba(0,0,0,0.08)',
                            cursor: 'pointer', transition: 'all 0.3s', padding: 0
                          }} />
                      );
                    })}
                  </div>
                  <button onClick={goNext} style={arrowBtn} title="Next board">{arrowSvg('right')}</button>
                </div>
              );
            })() : (
              <div style={{ width: 56, height: 3, background: '#e8a838', borderRadius: 2, margin: '8px auto' }} />
            )}
            {activeBoard?.tournament && (
              <div style={{ fontSize: 20, fontWeight: 600, color: '#0d1b2a' }}>
                {activeBoard.tournament.name.replace(/\s*\(Day\s*\d+\)\s*$/, '')}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Scores table zone */}
      <div style={{
        '--th-bg':      event.scoreboard_table_header_bg     || '#e8a838',
        '--th-color':   event.scoreboard_table_header_color  || '#0d1b2a',
        '--row-color':  event.scoreboard_table_row_color     || '#0d1b2a',
        '--row-border': event.scoreboard_table_row_border    || '#eeeeee',
        background: '#ffffff',
        padding: isAdBoard ? 0 : '20px 24px 28px',
        maxWidth: isAdBoard ? '100%' : 1400,
        margin: '0 auto'
      }}>
        {allBoards.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '80px 0', color: '#8a9bb5', fontSize: '1.1rem' }}>
            No tournaments set up yet.
          </div>
        ) : isAdBoard ? (
          <AdvertBoard board={activeBoard} isVisible={visible} />
        ) : activeBoard?.type === 'calcutta_daily' ? (
          <CalcuttaDailyBoard board={activeBoard} event={event} isVisible={visible} duration={activeBoard?._duration} />
        ) : activeBoard?.type === 'calcutta' ? (
          <CalcuttaBoard board={activeBoard} event={event} isVisible={visible} duration={activeBoard?._duration} />
        ) : activeBoard?.type === 'daily_winners' ? (
          <DailyWinnersBoard board={activeBoard} event={event} isVisible={visible} duration={activeBoard?._duration} />
        ) : activeBoard?.type === 'per_catch' ? (
          <PerCatchBoard board={activeBoard} event={event} isVisible={visible} duration={activeBoard?._duration} />
        ) : (
          <TournamentBoard board={activeBoard} event={event} isVisible={visible} duration={activeBoard?._duration} />
        )}
      </div>

      {/* Sponsor banner (dark navy, never customizable) */}
      {hasSponsorBar && !isAdBoard && <SponsorBanner sponsors={sponsors} eventId={eventId} />}
    </div>
  );
}
